import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useWorkspaces, type WorkspaceSummary } from "./api";
import { useAuth } from "../auth/AuthProvider";

interface WorkspaceState {
  current: WorkspaceSummary | null;
  setSlug: (slug: string) => void;
  workspaces: WorkspaceSummary[];
  loading: boolean;
}

const WorkspaceContext = createContext<WorkspaceState | null>(null);

const STORAGE_KEY = "ml.current-workspace-slug";

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  // Don't fetch /api/workspaces until we have an authenticated user — on
  // public routes (login, accept-invite) that call would 401 and trip the
  // api-client's session-expired redirect.
  const { data, isLoading } = useWorkspaces({ enabled: !!user });
  const [slug, setSlug] = useState<string | null>(() =>
    typeof window === "undefined" ? null : window.localStorage.getItem(STORAGE_KEY)
  );

  const workspaces = data?.workspaces ?? [];

  // Default to the user's home workspace; superadmins remember their last pick.
  useEffect(() => {
    if (!user) return;
    if (!user.isSuperadmin) {
      const own = workspaces.find((w) => w.id === user.workspaceId);
      if (own && own.slug !== slug) setSlug(own.slug);
      return;
    }
    if (slug && workspaces.some((w) => w.slug === slug)) return;
    if (workspaces[0]) setSlug(workspaces[0].slug);
  }, [user, workspaces, slug]);

  useEffect(() => {
    if (!slug) return;
    window.localStorage.setItem(STORAGE_KEY, slug);
  }, [slug]);

  const current = useMemo(
    () => workspaces.find((w) => w.slug === slug) ?? null,
    [workspaces, slug]
  );

  const value = useMemo<WorkspaceState>(
    () => ({ current, setSlug, workspaces, loading: isLoading }),
    [current, workspaces, isLoading]
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace(): WorkspaceState {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error("useWorkspace must be used inside <WorkspaceProvider>");
  return ctx;
}
